import Image from "next/image";
import type { Image as ShopImage } from "@/lib/shopify/types";
import { cn } from "@/lib/format";
import { Seal } from "./Seal";

type Props = {
  image: ShopImage | null | undefined;
  alt: string;
  sizes: string;
  preload?: boolean;
  // "contain" shows the whole photo with room around it, for product shots
  // in a frame whose shape doesn't match theirs.
  fit?: "cover" | "contain";
  className?: string;
};

// Every product picture sits on a tissue tile. The photo is multiplied onto
// it, so the white backgrounds of product shots turn linen and match the demo
// illustrations. Inside a card (`group/card`) the photo eases in on hover.
export function ProductImage({ image, alt, sizes, preload = false, fit = "cover", className }: Props) {
  if (!image) {
    return (
      <div className={cn("flex items-center justify-center bg-tissue", className)} role="img" aria-label={alt}>
        <Seal className="w-1/4 max-w-16 opacity-25" />
      </div>
    );
  }
  return (
    <div className={cn("overflow-hidden bg-tissue", className)}>
      <Image
        src={image.url}
        alt={image.altText || alt}
        width={image.width}
        height={image.height}
        sizes={sizes}
        preload={preload}
        unoptimized={!image.url.startsWith("https://cdn.shopify.com/")}
        className={cn(
          "size-full mix-blend-multiply transition-transform duration-700 ease-out group-hover/card:scale-[1.035]",
          fit === "contain" ? "object-contain p-[9%]" : "object-cover",
        )}
      />
    </div>
  );
}
